import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "~app/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 data-[loading=true]:opacity-100 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        base: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        default: "bg-brand-500 text-white shadow-xs hover:bg-brand-600",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
      loading?: boolean;
      loadingProgress?: boolean;
      loadingProgressValue?: number;
      loadingProgressClassName?: string;
    }
>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingProgress = false,
      loadingProgressValue,
      loadingProgressClassName,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const internalProgress = useButtonProgress(loading && loadingProgress);
    const hasExternalProgress =
      typeof loadingProgressValue === "number" &&
      Number.isFinite(loadingProgressValue);
    const progress = hasExternalProgress
      ? Math.max(0, Math.min(100, loadingProgressValue ?? 0))
      : internalProgress;
    const stableProgress = useForwardOnlyProgress({
      active: loading && loadingProgress,
      progress,
    });

    return (
      <Comp
        ref={ref}
        data-slot="button"
        data-loading={loading ? "true" : undefined}
        className={cn(
          buttonVariants({ variant, size, className }),
          "cursor-pointer",
          loadingProgress ? "relative overflow-hidden" : null
        )}
        disabled={loading || props.disabled}
        {...props}
      >
        {loadingProgress && stableProgress > 0 && (
          <ButtonLoadingFill
            progress={stableProgress}
            className={loadingProgressClassName}
          />
        )}
        <span className="relative z-[1] inline-flex items-center justify-center gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {children}
        </span>
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };

function ButtonLoadingFill({
  progress,
  className,
}: {
  progress: number;
  className?: string;
}) {
  if (progress <= 0) {
    return null;
  }

  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
    >
      <span
        className={cn(
          "absolute inset-y-0 left-0 rounded-[inherit] bg-brand-500 transition-[width] duration-300 ease-out",
          className
        )}
        style={{ width: `${progress}%` }}
      >
        <span className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0.1)_28%,rgba(255,255,255,0)_62%)] opacity-70 mix-blend-soft-light animate-button-fill-wave" />
      </span>
    </span>
  );
}

function useButtonProgress(active: boolean) {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    if (!active) return undefined;
    let value = 0;
    setProgress(0);

    const tick = () => {
      value = Math.min(value + Math.random() * 10 + 8, 92);
      setProgress(value);
      interval = window.setTimeout(tick, 260);
    };

    let interval = window.setTimeout(tick, 140);

    return () => {
      window.clearTimeout(interval);
    };
  }, [active]);

  React.useEffect(() => {
    if (active || progress === 0) return undefined;
    // Let the fill complete before resetting so the motion feels intentional.
    setProgress(100);
    const timeout = window.setTimeout(() => setProgress(0), 240);
    return () => window.clearTimeout(timeout);
  }, [active, progress]);

  return progress;
}

function useForwardOnlyProgress({
  active,
  progress,
}: {
  active: boolean;
  progress: number;
}) {
  const [displayProgress, setDisplayProgress] = React.useState(0);
  const exitTimeout = React.useRef<number>();
  const peakRef = React.useRef(0);

  React.useEffect(() => {
    window.clearTimeout(exitTimeout.current);

    if (active) {
      setDisplayProgress((prev) => {
        const next = Math.max(prev, progress, peakRef.current);
        peakRef.current = next;
        return next;
      });
      return () => window.clearTimeout(exitTimeout.current);
    }

    if (displayProgress === 0) return;

    setDisplayProgress(100);
    exitTimeout.current = window.setTimeout(() => setDisplayProgress(0), 240);
    peakRef.current = 0;

    return () => window.clearTimeout(exitTimeout.current);
  }, [active, progress, displayProgress]);

  return displayProgress;
}
